import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Search, Sparkles, Calendar, CreditCard, Star, ArrowRight,
  ChefHat, Brush, Baby, BookOpen, Dumbbell, Scissors,
  Wrench, Flower, PawPrint, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ProviderCard } from '../components/provider/ProviderCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMAGE = 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?crop=entropy&cs=srgb&fm=jpg&q=85';

const CATEGORY_ICONS = {
  chef: ChefHat,
  cleaning: Brush,
  babysitting: Baby,
  tutoring: BookOpen,
  fitness: Dumbbell,
  beauty: Scissors,
  handyman: Wrench,
  gardening: Flower,
  pet_care: PawPrint,
  elderly_care: Heart,
};

export default function Home() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [featuredProviders, setFeaturedProviders] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await fetch(`${API_URL}/api/seed`, { method: 'POST' });
        
        // Fetch categories
        const categoriesRes = await fetch(`${API_URL}/api/config/categories`);
        if (categoriesRes.ok) {
          const data = await categoriesRes.json();
          setCategories(data.categories);
        }
        
        // Fetch featured providers
        const providersRes = await fetch(`${API_URL}/api/providers?limit=4`);
        if (providersRes.ok) {
          const providers = await providersRes.json();
          setFeaturedProviders(providers);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCategory && selectedCategory !== 'all') params.set('category', selectedCategory);
    navigate(`/providers?${params.toString()}`);
  };

  const getCategoryName = (key) => {
    return categories[key]?.[language] || categories[key]?.['en'] || key;
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div 
          className="hero-background"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="hero-overlay" />
        
        <div className="relative z-10 container-app flex flex-col justify-center min-h-[90vh] pt-20 pb-16">
          <div className="max-w-3xl animate-fade-in-up">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {t('home.heroTitle')}{' '}
              <span className="text-accent">{t('home.heroTitleAccent')}</span>
            </h1>
            <p className="text-white/80 text-lg sm:text-xl mb-10 max-w-2xl">
              {t('home.heroSubtitle')}
            </p>
          </div>
          
          {/* Search Box */}
          <form 
            onSubmit={handleSearch}
            className="search-container max-w-4xl animate-fade-in-up stagger-2"
            data-testid="search-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-5">
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  {t('home.search')}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder={t('home.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-12"
                    data-testid="search-input"
                  />
                </div>
              </div>
              
              <div className="md:col-span-4">
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  {t('nav.services')}
                </label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="h-12" data-testid="category-select">
                    <SelectValue placeholder={t('home.allCategories')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('home.allCategories')}</SelectItem>
                    {Object.keys(categories).map((key) => (
                      <SelectItem key={key} value={key}>
                        {getCategoryName(key)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="md:col-span-3 flex items-end">
                <Button 
                  type="submit" 
                  className="btn-primary w-full h-12"
                  data-testid="search-submit"
                >
                  <Search className="w-4 h-4 mr-2" />
                  {t('home.search')}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-16 bg-muted/30" data-testid="categories-section">
        <div className="container-app">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl font-semibold mb-3">
              {t('nav.services')}
            </h2>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {Object.keys(categories).map((key) => {
              const Icon = CATEGORY_ICONS[key] || Sparkles;
              return (
                <button
                  key={key}
                  onClick={() => navigate(`/providers?category=${key}`)}
                  className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-card border border-border/50 hover:border-accent hover:shadow-medium transition-all hover-lift"
                  data-testid={`category-${key}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-accent" />
                  </div>
                  <span className="text-sm font-medium text-center">{getCategoryName(key)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-background noise-overlay" data-testid="how-it-works">
        <div className="container-app relative z-10">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold mb-4">
              {t('home.howItWorks')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('home.howItWorksSubtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Sparkles,
                title: t('home.step1Title'),
                description: t('home.step1Desc')
              },
              {
                icon: Calendar,
                title: t('home.step2Title'),
                description: t('home.step2Desc')
              },
              {
                icon: CreditCard,
                title: t('home.step3Title'),
                description: t('home.step3Desc')
              }
            ].map((step, index) => (
              <div 
                key={index}
                className="text-center p-8 rounded-2xl bg-card border border-border/50 hover-lift"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-6">
                  <step.icon className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-3">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Providers */}
      <section className="py-20" data-testid="featured-providers">
        <div className="container-app">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold mb-3">
                {t('home.featuredProviders')}
              </h2>
              <p className="text-muted-foreground">
                {t('home.featuredSubtitle')}
              </p>
            </div>
            <Button 
              variant="ghost" 
              className="hidden sm:flex items-center gap-2 text-accent hover:text-accent"
              onClick={() => navigate('/providers')}
              data-testid="view-all-providers"
            >
              {t('home.viewAll')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="aspect-[4/5] rounded-2xl skeleton" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProviders.map((provider) => (
                <ProviderCard key={provider.provider_id} provider={provider} />
              ))}
            </div>
          )}
          
          <div className="mt-10 text-center sm:hidden">
            <Button 
              variant="outline"
              className="w-full"
              onClick={() => navigate('/providers')}
            >
              {t('home.viewAll')}
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-charcoal text-white" data-testid="stats-section">
        <div className="container-app">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "100+", label: t('home.stats.providers') },
              { value: "2000+", label: t('home.stats.bookings') },
              { value: "4.8", label: t('home.stats.avgRating'), icon: Star },
              { value: "Hamburg", label: t('home.stats.coverage') }
            ].map((stat, index) => (
              <div key={index} className="animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="flex items-center justify-center gap-2 mb-2">
                  {stat.icon && <stat.icon className="w-6 h-6 text-accent fill-accent" />}
                  <span className="font-heading text-4xl font-bold">{stat.value}</span>
                </div>
                <p className="text-white/60">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-accent/5" data-testid="cta-section">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold mb-4">
            {t('home.ctaTitle')}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            {t('home.ctaSubtitle')}
          </p>
          <Button 
            className="btn-primary"
            onClick={() => navigate('/register?role=provider')}
            data-testid="become-provider-cta"
          >
            {t('home.ctaButton')}
          </Button>
        </div>
      </section>
    </div>
  );
}
